import AppContext from 'App/AppContext';
import { IPIconButton, IPPivot, IPSeparator, IPText, IPTooltipHost } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import EditTextFieldCallOut from 'components/CallOuts/EditTextFieldCallOut';
import { ResizableSplit } from 'components/Utils/ResizableSplit';
import { cancelIcon, globalStackStylesHeight100, globalStackTokensGapSmall } from 'globalStyles';
import { KeyboardEvent, MouseEvent, ReactNode, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getLocalStorageData, LocalStorageKeys, setLocalStorageData } from 'utils/localstorage';
import { capitalizeFirstLetter, getRandomId } from 'utils/string';
import { getTaskViewModalStackStyles } from '../View/TaskRenderHelpers';
import { TaskIdBadge } from './Details/TaskIdBadge';
import { TaskAIPane } from './TaskAIPane';

const taskPanelDefaultWidth: number = 420;
const minPanelWidth: number = 280;
const maxPanelShare: number = 0.5;
//the main column always keeps this much room
const minMainWidth: number = 420;

// The pieces of the redesigned task dialog that normal tasks and events share: the stored state of the side
// panel, the header, the main column + panel layout and the tab strip of the panel.

export interface ITaskDetailPanelState {
  panelOpen: boolean;
  fullWindow: boolean;
  onChangePanelOpen: (open: boolean) => void;
  onToggleFullWindow: () => void;
}

export const useTaskDetailPanelState = (): ITaskDetailPanelState => {
  const appContext = useContext(AppContext);
  const [panelOpen, setPanelOpen] = useState<boolean>(
    () => getLocalStorageData(appContext, LocalStorageKeys.TaskDetailPanelOpen) !== '0',
  );
  const [fullWindow, setFullWindow] = useState<boolean>(
    () => getLocalStorageData(appContext, LocalStorageKeys.TaskFullWindow) === '1',
  );

  const onChangePanelOpen = (open: boolean) => {
    setPanelOpen(open);
    setLocalStorageData(appContext, LocalStorageKeys.TaskDetailPanelOpen, open ? '1' : '0', false);
  };

  const onToggleFullWindow = () => {
    const newValue = !fullWindow;
    setFullWindow(newValue);
    setLocalStorageData(appContext, LocalStorageKeys.TaskFullWindow, newValue ? '1' : '0', false);
  };

  return {
    panelOpen,
    fullWindow,
    onChangePanelOpen,
    onToggleFullWindow,
  };
};

interface ITaskNewViewHeaderProps {
  title: string;
  taskId: number;
  //whether the id badge shows next to the title
  showTaskId: boolean;
  canRename: boolean;
  onRename: (newName: string) => void;
  panelState: ITaskDetailPanelState;
  onClose: () => void;
  //the action buttons of the task type
  topBar: ReactNode;
  //error and info messages under the header
  messages?: ReactNode;
}

// Title (click to rename) + id badge left, the action buttons centred, full window and close right
export const TaskNewViewHeader = (props: ITaskNewViewHeaderProps) => {
  const { t } = useTranslation(['task']);
  const [showRenameCallOut, setShowRenameCallOut] = useState<boolean>(false);
  //the title tooltip only adds something when the title is cut off
  const [isTitleTruncated, setIsTitleTruncated] = useState<boolean>(false);
  const taskNameTitleId = useRef(`isoplanner-task-name-title-${getRandomId()}`).current;
  const fullWindow = props.panelState.fullWindow;

  const onTitleKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setShowRenameCallOut(true);
    }
  };

  return (
    <IPStack.Item>
      <IPStack horizontal verticalAlign="center" styles={{ root: { minWidth: 0 } }}>
        <IPStack.Item styles={{ root: { flex: '1 1 0', minWidth: 0 } }}>
          <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
            <IPStack.Item styles={{ root: { minWidth: 0 } }}>
              <IPTooltipHost content={isTitleTruncated ? props.title : undefined}>
                <IPText
                  id={taskNameTitleId}
                  onMouseEnter={(e: MouseEvent<HTMLElement>) =>
                    setIsTitleTruncated(e.currentTarget.scrollWidth > e.currentTarget.clientWidth)
                  }
                  block
                  nowrap
                  variant="xLarge"
                  role={props.canRename ? 'button' : undefined}
                  tabIndex={props.canRename ? 0 : undefined}
                  styles={props.canRename ? { root: { cursor: 'pointer' } } : undefined}
                  onClick={props.canRename ? () => setShowRenameCallOut(true) : undefined}
                  onKeyDown={props.canRename ? onTitleKeyDown : undefined}
                >
                  {capitalizeFirstLetter(props.title)}
                </IPText>
              </IPTooltipHost>
            </IPStack.Item>
            <IPStack.Item>
              <TaskIdBadge taskId={props.taskId} showTaskId={props.showTaskId} />
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
        <IPStack.Item styles={{ root: { flexShrink: 0 } }}>{props.topBar}</IPStack.Item>
        <IPStack.Item styles={{ root: { flex: '1 1 0', minWidth: 0 } }}>
          <IPStack horizontal horizontalAlign="end" verticalAlign="center">
            <IPStack.Item>
              <IPTooltipHost content={fullWindow ? t('task:Detail.ExitFullWindow') : t('task:Detail.FullWindow')}>
                <IPIconButton
                  iconProps={{ iconName: fullWindow ? 'BackToWindow' : 'FullScreen' }}
                  onClick={props.panelState.onToggleFullWindow}
                />
              </IPTooltipHost>
            </IPStack.Item>
            <IPStack.Item>
              <IPIconButton iconProps={cancelIcon} onClick={props.onClose} />
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
      </IPStack>
      <IPSeparator />
      {props.messages}
      <EditTextFieldCallOut
        targetId={taskNameTitleId}
        isVisible={showRenameCallOut}
        value={props.title}
        onClose={() => setShowRenameCallOut(false)}
        onUpdate={props.onRename}
        required={true}
        maxLenght={512}
      />
    </IPStack.Item>
  );
};

interface ITaskNewViewLayoutProps {
  panelState: ITaskDetailPanelState;
  header: ReactNode;
  main: ReactNode;
  panel: ReactNode;
  footer: ReactNode;
  //the AI assistant: a third column beside the task, opened by the criterion path or the AI
  //button
  sidePane?: ReactNode;
}

// Header, main column, resizable side panel (or its expand button), optional AI side pane and footer
export const TaskNewViewLayout = (props: ITaskNewViewLayoutProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['task']);
  const { panelOpen, fullWindow } = props.panelState;
  const hasSidePane = props.sidePane !== undefined;

  return (
    <IPStack
      verticalFill
      tokens={globalStackTokensGapSmall}
      styles={getTaskViewModalStackStyles(appContext.isMobileView, false, hasSidePane, fullWindow, true)}
    >
      {props.header}
      <IPStack.Item grow>
        <IPStack horizontal styles={globalStackStylesHeight100} tokens={globalStackTokensGapSmall}>
          <IPStack.Item grow styles={{ root: { minWidth: 0 } }}>
            <ResizableSplit
              main={props.main}
              pane={props.panel}
              isPaneOpen={panelOpen}
              collapsedPane={
                <IPStack.Item>
                  <IPTooltipHost content={t('task:Detail.ExpandPanel')}>
                    <IPIconButton
                      iconProps={{ iconName: 'DoubleChevronLeft' }}
                      onClick={() => props.panelState.onChangePanelOpen(true)}
                      styles={{ root: { height: 45 } }}
                    />
                  </IPTooltipHost>
                </IPStack.Item>
              }
              defaultWidth={taskPanelDefaultWidth}
              minPaneWidth={minPanelWidth}
              minMainWidth={minMainWidth}
              maxPaneShare={maxPanelShare}
              storageKey={LocalStorageKeys.TaskDetailPanelWidth}
              handleTitle={t('task:Detail.ResizePanel')}
            />
          </IPStack.Item>
          {hasSidePane && <TaskAIPane>{props.sidePane}</TaskAIPane>}
        </IPStack>
      </IPStack.Item>
      <IPStack.Item>
        <IPSeparator styles={{ root: { height: 1 } }} />
      </IPStack.Item>
      <IPStack.Item>{props.footer}</IPStack.Item>
    </IPStack>
  );
};

interface ITaskNewViewPanelTabsProps {
  selectedKey: string;
  onSelect: (key: string) => void;
  onCollapse: () => void;
  //the IPPivotItems
  children: ReactNode;
}

// The tab strip of the side panel, with the collapse button in front
export const TaskNewViewPanelTabs = (props: ITaskNewViewPanelTabsProps) => {
  const { t } = useTranslation(['task']);

  return (
    <IPStack.Item>
      <IPStack horizontal verticalAlign="start">
        <IPStack.Item>
          <IPTooltipHost content={t('task:Detail.CollapsePanel')}>
            <IPIconButton
              iconProps={{ iconName: 'DoubleChevronRight' }}
              onClick={props.onCollapse}
              styles={{ root: { height: 45 } }}
            />
          </IPTooltipHost>
        </IPStack.Item>
        <IPStack.Item grow styles={{ root: { minWidth: 0 } }}>
          <IPPivot
            selectedKey={props.selectedKey}
            onLinkClick={(item) => props.onSelect(item?.props.itemKey ?? 'TaskDetails')}
            overflowBehavior="menu"
          >
            {props.children}
          </IPPivot>
        </IPStack.Item>
      </IPStack>
    </IPStack.Item>
  );
};
